import React, { useState, useEffect, useCallback } from "react";
import { ThemeProvider } from "@emotion/react";
import { theme } from "./theme";
import WebFont from "webfontloader";

import { CcLayout } from "./elements/ccLayout";

import WelcomeScreen from "./pages/welcomeScreen";
import MainScreen from "./pages/mainScreen";
import ElementScreen from "./pages/elementScreen";
import ContactScreen from "./pages/contactScreen";
import {
  customJsonParser,
  getArrayLastItem,
  transformSnakeToCamelCase,
  sendMessageToParentFrame,
} from "./helpers/helpers";
import { debounce } from "underscore";

export const createConfigJSON = (settings) => {
  return {
    fonts: settings?.fonts || ["Roboto", "Arial"],
    colors: {
      primary: settings?.primaryColor || "#347CFF",
      secondary: settings?.secondaryColor || "#DEEAFF",
    },
    position: {
      side: settings?.location || "right",
      margin: settings?.marginInPx || 0,
      zIndex: settings?.zIndex || 1,
    },
    delay: settings?.delayInMiliseconds || 0,
    mobile: settings?.hideOnMobile || false,
  }
};

const ctxBaseObject = {
  welcomeScreen: {},
  sites: [],
  history: [],
  isMobile: null,
  landscape: false,
  height: "auto",
  updateContext: () => {},
}; 

export const CcRootContext = React.createContext(ctxBaseObject);

const ConversionCenterRoot = (props) => {
  const [ccData, setCcData] = useState(ctxBaseObject);
  const [config, setConfig] = useState(null);
  const [delayPassed, setDelayPassed] = useState(false);
  const [connected, setConnected] = useState(false);

  const updateContext = (data) => {
    setCcData((prevState) => ({
      ...prevState,
      ...data,
    }));
  };

  useEffect(() => {
    const pathId = new URL(window.location.href);
    const ccId = pathId.searchParams.get('uuid') || props.previewData?.attributes?.uuid;
    if (!props.preview) {
      fetch(`/api/popups/${ccId}`)
        .then((resp) => resp.json())
        .then((data) => {
          const parsedJson = customJsonParser(data, transformSnakeToCamelCase);
          const config = createConfigJSON(parsedJson.settings?.attributes);
          setConfig(config);
          setCcData((prevState) => ({
            ...prevState,
            ...parsedJson.settings,
            draftId: ccId,
            updateContext: updateContext,
          }));
          WebFont.load({
            google: {
              families: [...config.fonts],
            },
          });
        })
        .catch((error) => console.error(error));

      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          sendMessageToParentFrame({ 'height': entry.contentRect.height})
        }
      });
      
      resizeObserver.observe(document.getElementById('ccRoot'));
    } else {
      const parsedJson = customJsonParser({settings: props.previewData.settings}, transformSnakeToCamelCase);
      const config = createConfigJSON(parsedJson.settings?.attributes);
      setConfig(config);
      setCcData((prevState) => ({
        ...prevState,
        ...parsedJson.settings,
        draftId: parsedJson.settings.attributes.uuid,
        updateContext: updateContext,
        isMobile: false,
        preview: props.preview,
      }));
    }
  }, [props.preview, props.previewData, props.previewData?.attributes?.uuid]);

  useEffect(() => {
    if (!config) return;
    const ccDeleay = setTimeout(() => {
      setDelayPassed(true);
    }, config.delay);

    return () => {
      clearTimeout(ccDeleay);
    }
  }, [config]);

  const setMobile = useCallback(
    debounce((event) => {
      setCcData((prevState) => ({
        ...prevState,
        isMobile: event.data.isMobile,
      }));
    }, 500),
    []
  );

  const setOrientation = useCallback(
    debounce((event) => {
      updateContext({
        landscape: event.data.landscape,
        height: event.data.height,
      });
    }, 500),
    []
  );

  useEffect(() => {
    if(!ccData?.attributes) return;
    const handlePostMessage = (event) => {
      if (event?.data?.source?.includes("react")) return;
      if (!ccData?.attributes?.websiteUrl?.includes(event.origin)) return;
      if (event?.data?.source !== "cc-parent-frame") return;
      if (event?.data.message === "checkIfMobile") setMobile(event);
      if (event?.data.message === "checkOrientation") setOrientation(event);
      if (event?.data.message === "test" && !connected) setConnected(true);
    }

    window.addEventListener("message", handlePostMessage, false);
    return () => {
      window.removeEventListener("message", handlePostMessage, false);
    }
  }, [ccData, connected, setMobile]);

  useEffect(() => {
    connected ? sendMessageToParentFrame("connected") : sendMessageToParentFrame("testing connection");
  }, [connected]);

  const getLastHistoryElement = () => getArrayLastItem(ccData.history);
  const isWelcomeScreenVisible = !ccData?.welcomeScreen?.disabled && !ccData.history.length;
  const isMainScreenVisible = ccData.history.length > 0 && getLastHistoryElement().activeComponentType === "mainScreen";
  const isElementScreenVisible = ccData.history.length > 0 && getLastHistoryElement().activeComponentType === "elementScreen";
  const isContactScreenVisible = ccData.history.length > 0 && getLastHistoryElement().activeComponentType === "contactScreen";

  const findFirstActiveSite = () => ccData.sites.find((site) => site.active);

  const mainScreenPreview = !props.defaultScreen;

  return(
    delayPassed && (
      <ThemeProvider theme={theme(config)}>
        <CcRootContext.Provider value={ccData}>
          {props.preview ? (
            <CcLayout
              toggleVisibility={() => sendMessageToParentFrame("hide")}
            >
              {isWelcomeScreenVisible && mainScreenPreview ? (
                <WelcomeScreen />
              ) : (
                !ccData.history.length && !props?.defaultScreen?.activeComponentType && (
                  <MainScreen
                    defaultScreen={props?.defaultScreen || {
                      activeComponentType: findFirstActiveSite()?.contentType,
                      activeSpecialty: findFirstActiveSite()?.specialtyName,
                      activeElementPosition: findFirstActiveSite()?.welcomeScreenButton.numberInOrder,
                    }}
                  />
                )
              )}
              {props?.defaultScreen?.activeComponentType && !ccData.history.length && (
                <>
                  {props?.defaultScreen?.activeComponentType === "mainScreen" ? <MainScreen defaultScreen={props.defaultScreen}/> : null}
                  {props?.defaultScreen?.activeComponentType === "elementScreen" ? <ElementScreen defaultScreen={props.defaultScreen}/> : null}
                  {props?.defaultScreen?.activeComponentType === "contactScreen" ? <ContactScreen defaultScreen={props.defaultScreen}/> : null}
                </>
              )}
              {ccData.history.length > 0 && (
                <>
                  {isWelcomeScreenVisible && <WelcomeScreen />}
                  {isMainScreenVisible && <MainScreen />}
                  {isElementScreenVisible && <ElementScreen />}
                  {isContactScreenVisible && <ContactScreen />}
                </>
              )}
            </CcLayout>
          ) : (
            <CcLayout
              toggleVisibility={() => sendMessageToParentFrame("hide")}
            >
              {ccData.history.length > 0 ? (
                <>
                  {isMainScreenVisible && <MainScreen />}
                  {isElementScreenVisible && <ElementScreen />}
                  {isContactScreenVisible && <ContactScreen />}
                </>
              ) : (
                <>
                  {isWelcomeScreenVisible && <WelcomeScreen />}
                  {!isWelcomeScreenVisible && <MainScreen />}
                </>
              )}
            </CcLayout>
          )}
        </CcRootContext.Provider>
      </ThemeProvider>
    )
  );
};

export default ConversionCenterRoot;
